import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NominaService, type Nomina, type PagoExtra } from '../services/nomina/nomina.service';
import { UsersService } from '../services/users/users.service';
import { usePermissions } from '../hooks/usePermissions';
import { CreateNominaDrawer } from '../components/nomina/CreateNominaDrawer';
import { NominaDetailDrawer } from '../components/nomina/NominaDetailDrawer';
import { ColillaPagoModal } from '../components/nomina/ColillaPagoModal';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { DatePickerInput } from '../components/ui/DatePickerInput';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import toast from 'react-hot-toast';
import {
  Banknote, Search, Plus, Calendar, User, CheckCircle, Clock,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Trash2, Eye, FileText, DollarSign, ChevronDown, Check, Gift, X
} from 'lucide-react';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);
};

const formatDate = (dateStr: string) => {
  const fecha = dateStr.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || dateStr;
  return new Date(`${fecha}T00:00:00-05:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
};

const TIPO_PAGO_EXTRA_LABELS: Record<string, string> = {
  FESTIVO: 'Festivo',
  HORA_EXTRA: 'Hora Extra',
  RECARGO_NOCTURNO: 'Recargo Nocturno',
  DOMINGO: 'Domingo',
  OTRO: 'Otro',
};

export const NominaPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  // Tabs
  const [activeTab, setActiveTab] = useState<'quincenas' | 'pagos-extras'>('quincenas');

  // --- Quincenas ---
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedNomina, setSelectedNomina] = useState<Nomina | null>(null);
  const [selectedColilla, setSelectedColilla] = useState<Nomina | null>(null);
  const [deleteNominaId, setDeleteNominaId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, estadoFiltro]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['nominas', page, limit, debouncedSearch, estadoFiltro],
    queryFn: () => NominaService.list(page, limit, {
      search: debouncedSearch || undefined,
      estado: estadoFiltro || undefined,
    }),
    staleTime: 60000,
    placeholderData: (previousData) => previousData,
  });

  const nominas = data?.data || [];
  const meta = data?.meta;

  const handlePagar = async (id: string) => {
    try {
      await NominaService.pagar(id);
      toast.success('Quincena marcada como pagada');
      refetch();
    } catch {
      toast.error('Error al marcar como pagada');
    }
  };

  const handleDelete = (id: string) => {
    setDeleteNominaId(id);
  };

  const confirmDelete = async () => {
    if (!deleteNominaId) return;
    setIsDeleting(true);
    try {
      await NominaService.delete(deleteNominaId);
      toast.success('Quincena eliminada');
      refetch();
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setIsDeleting(false);
      setDeleteNominaId(null);
      setSelectedNomina(null);
    }
  };

  // --- Pagos Extras ---
  const [pePage, setPePage] = useState(1);
  const [peLimit] = useState(10);
  const [peSearch, setPeSearch] = useState('');
  const [peDebouncedSearch, setPeDebouncedSearch] = useState('');
  const [peEstadoFiltro, setPeEstadoFiltro] = useState('');
  const [isPeCreateOpen, setIsPeCreateOpen] = useState(false);
  const [peDrawerVisible, setPeDrawerVisible] = useState(false);
  const [deletePagoExtraId, setDeletePagoExtraId] = useState<string | null>(null);
  const [isPeDeleting, setIsPeDeleting] = useState(false);

  useEffect(() => {
    if (isPeCreateOpen) {
      const t = setTimeout(() => setPeDrawerVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setPeDrawerVisible(false);
    }
  }, [isPeCreateOpen]);

  // Form crear pago extra
  const [peUsuarioId, setPeUsuarioId] = useState('');
  const [peTipo, setPeTipo] = useState<PagoExtra['tipo']>('FESTIVO');
  const [peMonto, setPeMonto] = useState('');
  const [peFecha, setPeFecha] = useState('');
  const [peDescripcion, setPeDescripcion] = useState('');
  const [isPeSubmitting, setIsPeSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPeDebouncedSearch(peSearch), 500);
    return () => clearTimeout(t);
  }, [peSearch]);

  useEffect(() => { setPePage(1); }, [peDebouncedSearch, peEstadoFiltro]);

  const { data: peData, isLoading: peLoading, isFetching: peFetching, refetch: peRefetch } = useQuery({
    queryKey: ['pagos-extras', pePage, peLimit, peDebouncedSearch, peEstadoFiltro],
    queryFn: () => NominaService.listPagosExtras(pePage, peLimit, {
      search: peDebouncedSearch || undefined,
      estado: peEstadoFiltro || undefined,
    }),
    staleTime: 60000,
    placeholderData: (previousData) => previousData,
  });

  const pagosExtras = peData?.data || [];
  const peMeta = peData?.meta;

  const { data: usuariosData } = useQuery({
    queryKey: ['users-vendedores-nomina'],
    queryFn: async () => {
      const res = await UsersService.getUsers(1, 100);
      return res.data;
    },
  });
  const usuarios = (usuariosData || []) as Array<{ id: string; nombreCompleto: string }>;

  const handlePePagar = async (id: string) => {
    try {
      await NominaService.pagarPagoExtra(id);
      toast.success('Pago extra marcado como pagado');
      peRefetch();
    } catch {
      toast.error('Error al marcar como pagado');
    }
  };

  const handlePeDelete = (id: string) => {
    setDeletePagoExtraId(id);
  };

  const confirmPeDelete = async () => {
    if (!deletePagoExtraId) return;
    setIsPeDeleting(true);
    try {
      await NominaService.deletePagoExtra(deletePagoExtraId);
      toast.success('Pago extra eliminado');
      peRefetch();
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setIsPeDeleting(false);
      setDeletePagoExtraId(null);
    }
  };

  const closePeDrawer = () => {
    setPeDrawerVisible(false);
    setTimeout(() => setIsPeCreateOpen(false), 300);
  };

  const submitPeCreate = async () => {
    if (!peUsuarioId || !peMonto || !peFecha) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    setIsPeSubmitting(true);
    try {
      await NominaService.createPagoExtra({
        usuarioId: peUsuarioId,
        tipo: peTipo,
        monto: Number(peMonto),
        fecha: peFecha,
        descripcion: peDescripcion || undefined,
      });
      toast.success('Pago extra creado');
      setIsPeCreateOpen(false);
      setPeUsuarioId('');
      setPeMonto('');
      setPeFecha('');
      setPeDescripcion('');
      setPeTipo('FESTIVO');
      peRefetch();
    } catch {
      toast.error('Error al crear pago extra');
    } finally {
      setIsPeSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Nómina</h1>
        <p className="text-sm text-gray-500">Gestión de pagos quincenales e ingresos puntuales de colaboradores</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('quincenas')}
          className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'quincenas'
              ? 'text-emerald-700 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700 border-transparent'
          }`}
        >
          Quincenas
        </button>
        <button
          onClick={() => setActiveTab('pagos-extras')}
          className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'pagos-extras'
              ? 'text-emerald-700 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700 border-transparent'
          }`}
        >
          Pagos Extras
        </button>
      </div>

      {activeTab === 'quincenas' ? (
        <>
          {/* Filtros + Botón Quincenas */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <div className="relative flex-1 min-w-[280px]">
              {isFetching && search ? (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent" />
              ) : (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              )}
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por colaborador..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-gray-500 text-sm bg-white"
              />
            </div>
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500"
            >
              <option value="">Todos los estados</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PAGADA">Pagada</option>
            </select>
            {hasPermission('Nomina', 'crear') && (
              <button
                onClick={() => setIsCreateOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Nueva Quincena
              </button>
            )}
          </div>

          {/* Tabla Quincenas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Colaborador</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Periodo</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Días / Domingos</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Devengado</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Descuentos</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Estado</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">Total a pagar</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {isLoading && nominas.length === 0 ? (
                    <tr><td colSpan={8} className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" /></td></tr>
                  ) : nominas.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-500">
                        <Banknote className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No se encontraron quincenas</p>
                      </td>
                    </tr>
                  ) : (
                    nominas.map((n) => (
                      <tr
                        key={n.id}
                        onClick={() => setSelectedNomina(n)}
                        className="hover:bg-emerald-50/50 transition-colors cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gray-100">
                              <User className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{n.usuario?.nombreCompleto || '—'}</p>
                              <p className="text-xs text-gray-500">{n.usuario?.email || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700">{formatDate(n.periodoInicio)} — {formatDate(n.periodoFin)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {n.diasLaborados}d / {n.domingosTrabajados} dom
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {formatCurrency(n.totalDevengado)}
                        </td>
                        <td className="px-6 py-4 text-sm text-red-600 font-medium">
                          {formatCurrency(n.totalDescuentos)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                            n.estado === 'PAGADA'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {n.estado === 'PAGADA' ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                            {n.estado === 'PAGADA' ? 'Pagada' : 'Pendiente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <p className="font-bold text-lg text-gray-900">{formatCurrency(n.totalPagar)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedColilla(n); }}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Ver colilla"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación Quincenas */}
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {((meta.page - 1) * meta.limit) + 1} — {Math.min(meta.page * meta.limit, meta.total)} de {meta.total} resultados
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(1)} disabled={page === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 px-2">Página {page} de {meta.totalPages}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page === meta.totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(meta.totalPages)} disabled={page === meta.totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Filtros + Botón Pagos Extras */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <div className="relative flex-1 min-w-[280px]">
              {peFetching && peSearch ? (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent" />
              ) : (
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              )}
              <input
                type="text"
                value={peSearch}
                onChange={(e) => setPeSearch(e.target.value)}
                placeholder="Buscar por colaborador..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:border-gray-500 text-sm bg-white"
              />
            </div>
            <select
              value={peEstadoFiltro}
              onChange={(e) => setPeEstadoFiltro(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500"
            >
              <option value="">Todos los estados</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PAGADO">Pagado</option>
            </select>
            {hasPermission('Nomina', 'crear') && (
              <button
                onClick={() => setIsPeCreateOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Nuevo Pago Extra
              </button>
            )}
          </div>

          {/* Tabla Pagos Extras */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Colaborador</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Fecha</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Tipo</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Descripción</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Estado</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">Monto</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {peLoading && pagosExtras.length === 0 ? (
                    <tr><td colSpan={7} className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" /></td></tr>
                  ) : pagosExtras.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500">
                        <Gift className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>No se encontraron pagos extras</p>
                      </td>
                    </tr>
                  ) : (
                    pagosExtras.map((pe) => (
                      <tr key={pe.id} className="hover:bg-emerald-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gray-100">
                              <User className="w-5 h-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{pe.usuario?.nombreCompleto || '—'}</p>
                              <p className="text-xs text-gray-500">{pe.usuario?.email || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-700">{formatDate(pe.fecha)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            {TIPO_PAGO_EXTRA_LABELS[pe.tipo] || pe.tipo}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                          {pe.descripcion || '—'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                            pe.estado === 'PAGADO'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {pe.estado === 'PAGADO' ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                            {pe.estado === 'PAGADO' ? 'Pagado' : 'Pendiente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <p className="font-bold text-lg text-gray-900">{formatCurrency(pe.monto)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-1">
                            {pe.estado === 'PENDIENTE' && hasPermission('Nomina', 'editar') && (
                              <button
                                onClick={() => handlePePagar(pe.id)}
                                className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                title="Marcar como pagado"
                              >
                                <DollarSign className="w-4 h-4" />
                              </button>
                            )}
                            {hasPermission('Nomina', 'eliminar') && (
                              <button
                                onClick={() => handlePeDelete(pe.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación Pagos Extras */}
            {peMeta && peMeta.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {((peMeta.page - 1) * peMeta.limit) + 1} — {Math.min(peMeta.page * peMeta.limit, peMeta.total)} de {peMeta.total} resultados
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPePage(1)} disabled={pePage === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPePage(p => p - 1)} disabled={pePage === 1} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 px-2">Página {pePage} de {peMeta.totalPages}</span>
                  <button onClick={() => setPePage(p => p + 1)} disabled={pePage === peMeta.totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPePage(peMeta.totalPages)} disabled={pePage === peMeta.totalPages} className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30">
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Drawers/Modals Quincenas */}
      <CreateNominaDrawer
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => { refetch(); setIsCreateOpen(false); }}
      />

      {selectedNomina && (
        <NominaDetailDrawer
          nomina={selectedNomina}
          onClose={() => setSelectedNomina(null)}
          onPagar={handlePagar}
          onDelete={handleDelete}
        />
      )}

      {selectedColilla && (
        <ColillaPagoModal
          nomina={selectedColilla}
          onClose={() => setSelectedColilla(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteNominaId}
        onClose={() => setDeleteNominaId(null)}
        onConfirm={confirmDelete}
        title="Eliminar Quincena"
        message="¿Estás seguro de que deseas eliminar esta quincena? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Drawer Crear Pago Extra */}
      {isPeCreateOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${peDrawerVisible ? 'opacity-100' : 'opacity-0'}`}
            onClick={closePeDrawer}
          />
          <div
            className={`relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              peDrawerVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Nuevo Pago Extra</h2>
              <button onClick={closePeDrawer} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Colaborador */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Colaborador</label>
                <Listbox value={peUsuarioId} onChange={setPeUsuarioId}>
                  <div className="relative">
                    <ListboxButton className="relative w-full cursor-pointer rounded-lg border border-gray-200 bg-white py-2.5 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all">
                      <span className="block truncate">
                        {peUsuarioId ? usuarios.find((u) => u.id === peUsuarioId)?.nombreCompleto : 'Seleccionar colaborador'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      transition
                      className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                      {usuarios.map((u) => (
                        <ListboxOption
                          key={u.id}
                          value={u.id}
                          className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                        >
                          <span className="block truncate">{u.nombreCompleto}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                            <Check className="h-4 w-4" />
                          </span>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de pago</label>
                <Listbox value={peTipo} onChange={setPeTipo}>
                  <div className="relative">
                    <ListboxButton className="relative w-full cursor-pointer rounded-lg border border-gray-200 bg-white py-2.5 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all">
                      <span className="block truncate">{TIPO_PAGO_EXTRA_LABELS[peTipo]}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      transition
                      className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                      {Object.entries(TIPO_PAGO_EXTRA_LABELS).map(([value, label]) => (
                        <ListboxOption
                          key={value}
                          value={value}
                          className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                        >
                          <span className="block truncate">{label}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                            <Check className="h-4 w-4" />
                          </span>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>

              {/* Monto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Monto</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={peMonto ? Number(peMonto).toLocaleString('es-CO') : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      setPeMonto(raw);
                    }}
                    placeholder="150.000"
                    className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha</label>
                <DatePickerInput
                  value={peFecha}
                  onChange={setPeFecha}
                  placeholder="Seleccionar fecha"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción (opcional)</label>
                <textarea
                  value={peDescripcion}
                  onChange={(e) => setPeDescripcion(e.target.value)}
                  placeholder="Ej: Festivo trabajado 19 de mayo"
                  rows={3}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={submitPeCreate}
                disabled={isPeSubmitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
              >
                {isPeSubmitting ? 'Guardando...' : 'Guardar Pago Extra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete pago extra */}
      <ConfirmModal
        isOpen={!!deletePagoExtraId}
        onClose={() => setDeletePagoExtraId(null)}
        onConfirm={confirmPeDelete}
        title="Eliminar Pago Extra"
        message="¿Estás seguro de que deseas eliminar este pago extra? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        isLoading={isPeDeleting}
      />
    </div>
  );
};

export default NominaPage;
